/**
 * The UT system backend's response shapes, and the adapters that unwrap them.
 *
 * This file is the *only* place that should know these shapes exist. Everything
 * above it works with plain domain objects and `PagedResponse<T>`.
 *
 * The backend has accumulated four distinct envelopes. They are documented here
 * with their source so the next person does not have to re-derive them:
 *
 *  1. `GeneralResponse<T>`          — the dominant one.
 *     UT system/Controllers/V1/Responses/GeneralResponse.cs
 *  2. Bare object, no envelope      — e.g. POST /api/Users/login returns
 *     `AuthToken` directly, despite its [ProducesResponseType] claiming
 *     otherwise. UT system/Controllers/V1/UsersController.cs:112
 *  3. `GeneralErrorResponse`        — a few catalogue endpoints' 404s.
 *     UT system/Controllers/V1/Responses/GeneralErrorResponse.cs
 *  4. RFC 7807 ValidationProblemDetails — emitted by [ApiController] model
 *     validation *before* controller code runs.
 *
 * Plus a fifth for failures: the global exception handler in
 * UT system/Startup.cs:86-136 writes a flatter `{ Code, Success, MessageEn,
 * MessageAr }` with no `data`. A failed login is not one of these: it is a 401
 * camelCase GeneralResponse with code `InvalidCredentials`.
 */

/** Envelope 1. Note `Errors` is `Record<string, string>` — singular strings. */
export interface GeneralResponse<T> {
  success: boolean;
  messageAr?: string | null;
  messageEn?: string | null;
  /**
   * An untyped string carrying one of three vocabularies depending on the call
   * site: an HttpStatusCode name ("OK", "NotFound"), a ValidationCodes member
   * ("Valdata1"), a CommunicationCodes member ("BALN1"), or a bare numeric
   * string ("422"). Do not switch on it without checking the specific endpoint.
   */
  code?: string | null;
  errors?: Record<string, string> | null;
  data: T;
}

/** Envelope 3. */
export interface GeneralErrorResponse {
  errorId?: string | null;
  errorCode?: string | null;
  message?: string | null;
}

/** Envelope 4. Note `errors` values are arrays here, unlike envelope 1. */
export interface ValidationProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  traceId?: string;
  errors?: Record<string, string[]>;
}

/** Envelope 5 — the global exception handler's shape. */
export interface GlobalErrorResponse {
  Code?: string;
  Success?: boolean;
  MessageEn?: string;
  MessageAr?: string;
}

/** UT system/Shared/PaginationMetadata.cs */
export interface PaginationMetadata {
  totalCount: number;
  pageSize: number;
  currentPage: number;
  hasNext: boolean;
  hasPrevious: boolean;
  totalPages: number;
  links?: Record<string, string>;
}

/**
 * Type guard for envelope 1.
 *
 * Checks `success` and `data` rather than just `data`, because a bare payload
 * could coincidentally carry a `data` property of its own.
 */
export function isGeneralResponse<T>(payload: unknown): payload is GeneralResponse<T> {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'success' in payload &&
    typeof (payload as { success: unknown }).success === 'boolean' &&
    'data' in payload
  );
}

/**
 * Unwrap a response that may or may not be enveloped.
 *
 * Several endpoints are inconsistent about this, so rather than tracking which
 * is which across ~40 controllers, unwrap defensively at the boundary.
 */
export function unwrap<T>(payload: GeneralResponse<T> | T): T {
  return isGeneralResponse<T>(payload) ? payload.data : payload;
}
